import { PlanConsistencyStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  PlanTypeCardAttention,
  type PlanTypeCardPlanRecord,
  type PlanTypeCardSubmissionSummary,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Badge,
  CardHeader,
  CardRoot,
  HStack,
  Link,
  Stack,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { PLAN_CONSISTENCY_STATUS_SPEC } from '~/components/common/PlanConsistencyStatusBadge'
import { PlanSubmissionStatusBadge } from '~/components/common/PlanSubmissionStatusBadge'
import { iconSize } from '~/constants/iconSize'
import { translateInterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import type { StatusBadgeVariant } from '~/models/planSubmission/statusBadge'
import { STATUS_BADGE_LABEL_KEYS } from '~/models/planSubmission/statusBadge'
import { PlanTypeCardBody } from '~/pages/SubmissionManagement/components/PlanTypeCardBody'
import { buildCardErrorRows } from '~/pages/SubmissionManagement/models/cardErrorRows'
import { formatTimestamp } from '~/pages/SubmissionManagement/models/formatTimestamp'
import type { KnownPlanTypeCardKind } from '~/pages/SubmissionManagement/models/planTypeCard'
import { PLAN_TYPE_CARD_TITLE_I18N_KEY } from '~/pages/SubmissionManagement/models/planTypeCard'
import { deriveProgressStageBadgeVariant } from '~/pages/SubmissionManagement/models/progressStage'
import { routePaths } from '~/routes/routePaths'

type Props = {
  /** UNSPECIFIED は呼び出し元 (`PlanTypeCardGrid`) が既に落として渡す。 */
  kind: KnownPlanTypeCardKind
  /** card body の元データ。計画レコード数 (Decision 6: 2 件以上でエラー行に BG コードを前置) もここから導く。 */
  planRecords: readonly PlanTypeCardPlanRecord[]
  /**
   * ヘッダー提出ステータス集約 (AC-PSM-008/009/010/053、Decision 2-7)。契約上
   * `cards` の全要素で必ず set (Design Note Contract/Data) だが、proto field は
   * optional のため未設定時は防御的に各要素を欠落として描画する
   * (= 主バッジなし・強調なし・エラー行なし・最終提出「—」。`isKnownPlanTypeCardKind`
   * と同型で、未知の状態を具体的な variant へ捏造しない)。
   */
  submissionSummary: PlanTypeCardSubmissionSummary | undefined
  /** ダッシュボードの対象日。取引先カードの遷移先 (提出記録の無い計画の業務キー) に使う。 */
  targetDate: Date
}

/**
 * `attention` (Decision 5) → ヘッダー全体の強調。
 *
 * - `background`: 強調背景。design `.plan-type-block.is-attention-{warn,error} .card-header`
 *   と同じ色系統 (差戻し・作成失敗・提出失敗・送信失敗 = negative / 不整合のみ = warning)
 * - `state`: ヘッダーの `data-attention`。強調はヘッダーの背景色でしか表現されず
 *   role / label で問い合わせる手段が無いため、強調の段階を属性で観測できるようにする
 *
 * 背景と属性を同じ entry から出し、強調が消える退行を属性で検出できるようにする。
 */
const HEADER_ATTENTION: Record<
  PlanTypeCardAttention,
  { background: string; state: 'warning' | 'error' } | undefined
> = {
  [PlanTypeCardAttention.UNSPECIFIED]: undefined,
  [PlanTypeCardAttention.WARNING]: {
    background: 'Surface.warningLow',
    state: 'warning',
  },
  [PlanTypeCardAttention.ERROR]: {
    background: 'Surface.negativeLow',
    state: 'error',
  },
}

/**
 * 主バッジ・重畳バッジ (不整合 / 承認待ち / 処理中) はいずれも一覧と同じ共有
 * `PlanSubmissionStatusBadge` で描く (Decision 4/10)。カードヘッダーでは警告
 * アイコンを出さない (注意喚起はヘッダー強調とエラー行が担う) ため hasWarning=false。
 */
const CardStatusBadge = ({
  variant,
}: {
  variant: StatusBadgeVariant
}): React.ReactElement => (
  <PlanSubmissionStatusBadge
    hasWarning={false}
    labelKeys={STATUS_BADGE_LABEL_KEYS}
    variant={variant}
  />
)

const INTERNAL_INCONSISTENCY_BADGE =
  PLAN_CONSISTENCY_STATUS_SPEC[
    PlanConsistencyStatus.INTERNAL_PLAN_INCONSISTENCY
  ]

/**
 * 計画内不整合バッジ (AC-PSM-077)。提出ステータスではないため、色と文言は一覧の
 * 整合ステータスと共有の `PLAN_CONSISTENCY_STATUS_SPEC` から取る。
 */
const InternalInconsistencyBadge = (): React.ReactElement => {
  const { t } = useTranslation()
  return (
    // 同じ warning の「不整合」(OCCTO 提出ステータス) と区別するため枠線を足し、タブの印
    // (TabIndicators) と同じ `FaTriangleExclamationSolid` を文言の前に置く (#9865 Des 判断。
    // design `plan/submission.html` のカードヘッダーの計画内不整合バッジ = icon 14px + 文言)
    <Badge
      alignItems="center"
      gap="4px"
      display="inline-flex"
      borderWidth="1px"
      borderColor="Border.warningMid"
      status="warning"
    >
      <FaTriangleExclamationSolid
        boxSize={iconSize.badgeInline}
        aria-hidden="true"
      />
      {t(INTERNAL_INCONSISTENCY_BADGE.labelKey)}
    </Badge>
  )
}

/**
 * 計画タイプカード 1 枚 (design `.card` 準拠、`design/ui-feasibility/components/card.md`)。
 *
 * ヘッダーは 計画タイプ名リンク (クリックで `/plan/procurement` を新規タブで開く、
 * AC-PSM-011、Decision 12: 3 カードとも同じ遷移先。一覧は URL クエリを受け取らない)
 * + 主バッジ (+ 不整合 / 計画内不整合 / 承認待ち / 処理中の重畳バッジ) + 右端の最終提出時刻 +
 * エラー行 (差戻し / 作成失敗 / 提出失敗 / 送信失敗) で構成する
 * (AC-PSM-008/009/010/053、Decision 2-7)。差戻し・作成失敗・不整合・送信失敗は
 * ヘッダー全体を強調する (Decision 5)。card-body (取引先カード一覧) は `PlanTypeCardBody` (AC-PSM-033/034/038)。
 */
export const PlanTypeCard = ({
  kind,
  planRecords,
  submissionSummary,
  targetDate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const title = t(PLAN_TYPE_CARD_TITLE_I18N_KEY[kind])
  // submissionSummary 欠落時は各 field を個別に防御する (progress_stage
  // UNSPECIFIED と同じ理由で、まとめて「未提出」等の具体的な summary に
  // 捏造しない)。
  const mainVariant =
    submissionSummary === undefined
      ? undefined
      : deriveProgressStageBadgeVariant(submissionSummary.progressStage)
  const errorRows = buildCardErrorRows(
    submissionSummary?.errors ?? [],
    planRecords.length,
  )
  const attention =
    submissionSummary?.attention ?? PlanTypeCardAttention.UNSPECIFIED
  const headerAttention = HEADER_ATTENTION[attention]

  return (
    <CardRoot
      overflow="hidden"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="radiiLg"
      boxShadow="shadSm"
    >
      <CardHeader
        padding="16px"
        background={headerAttention?.background}
        borderBottomWidth="1px"
        borderBottomColor="Border.lowEmphasis"
        data-attention={headerAttention?.state}
        // 強調はヘッダー全体の背景色でしか表現されず、role / label で
        // 問い合わせる手段が無いため、F5 test 用に card 単位で安定した
        // data-testid を持たせる。
        data-testid={`plan-type-card-header-${kind}`}
      >
        <VStack alignItems="stretch" gap={2}>
          <HStack justifyContent="space-between" flexWrap="wrap" gap={2}>
            <HStack flexWrap="wrap" gap={2}>
              <Link textStyle="headerMd" asChild>
                <RouterLink
                  rel="noopener noreferrer"
                  target="_blank"
                  to={routePaths.planProcurement.path}
                >
                  {title}
                </RouterLink>
              </Link>
              {mainVariant !== undefined && (
                <CardStatusBadge variant={mainVariant} />
              )}
              {submissionSummary?.hasInconsistent === true && (
                <CardStatusBadge variant="inconsistent" />
              )}
              {submissionSummary?.hasInternalInconsistency === true && (
                <InternalInconsistencyBadge />
              )}
              {submissionSummary?.hasApprovalPending === true && (
                <Link asChild>
                  <RouterLink to={routePaths.adminApprovalTasks.path}>
                    <CardStatusBadge variant="approvalPending" />
                  </RouterLink>
                </Link>
              )}
              {submissionSummary?.hasSubmitting === true && (
                <CardStatusBadge variant="submitting" />
              )}
            </HStack>
            <Text color="Text.midEmphasis" fontSize="sm" whiteSpace="nowrap">
              {t('page:submissionManagement.card.lastSubmittedAtLabel')}{' '}
              {/* AC-PSM-078: 版が無い計画は欄の値を「eBalance での提出記録なし」にする
                  (ラベルは残す。プロトタイプ #526) */}
              {submissionSummary?.noSubmissionRecord === true
                ? t('page:submissionManagement.card.noSubmissionRecord')
                : formatTimestamp(submissionSummary?.lastSubmittedAt)}
            </Text>
          </HStack>
          {errorRows.map((row) => {
            const [first, ...rest] = row.messages.map((message) =>
              message.kind === 'sendFailedCause'
                ? // AC-PSM-053: [短い区別] 1 文 (プロト #468 のカードのエラー行)
                  `[${t(message.shortKey)}] ${translateInterpolatedMessage(t, message.text)}`
                : message.kind === 'text'
                  ? message.text
                  : translateInterpolatedMessage(t, message.message),
            )
            // 1 件目 (代表 BG コードを前置) を title、 2 件目以降を description に渡す
            // (UI 辞書 components/alert-placement.md §4)
            return (
              <Alert
                key={row.key}
                description={
                  rest.length > 0 ? (
                    <Stack gap={0}>
                      {rest.map((text, restIndex) => (
                        // 行内メッセージは受付エラー配列由来で安定した一意 key を
                        // 持たないため index を使う (行自体の key は row.key)。
                        <Text key={`${row.key}-${restIndex + 1}`}>{text}</Text>
                      ))}
                    </Stack>
                  ) : undefined
                }
                status="error"
                title={
                  row.bgCodePrefix === undefined
                    ? first
                    : `[${row.bgCodePrefix}] ${first}`
                }
              />
            )
          })}
        </VStack>
      </CardHeader>
      <PlanTypeCardBody
        noSubmissionRecord={submissionSummary?.noSubmissionRecord === true}
        planRecords={planRecords}
        targetDate={targetDate}
      />
    </CardRoot>
  )
}
